import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductApi, CompanyApi, PersonalCabinetApi } from '../api';
import type { CompanyCategory } from '../api/PersonalCabinetApi';
import { Category, Product, Company } from '../types/Product';

interface ProductFormProps {
  product?: Product;
  selectedCompanyId?: number | null;
  latestProduct?: Product;
  lastCompanyProduct?: Product; // Last product added for this specific company
  onSaved: (savedProduct: Product) => void;
}

export default function ProductForm({ product, selectedCompanyId, latestProduct, lastCompanyProduct, onSaved }: ProductFormProps) {
  const { t } = useTranslation();

  const [formProduct, setFormProduct] = useState<Partial<Product>>(product || {});
  const [categories, setCategories] = useState<(Category | CompanyCategory)[]>([]);
  const [companiesAllowed, setCompaniesAllowed] = useState<Company[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string>('');
  const [duplicateBlocking, setDuplicateBlocking] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false); // Add loading state

  const checkDuplicatesRealTime = useCallback(async () => {
    if (!formProduct.firm || !formProduct.model || !formProduct.size || !selectedCompanyId) {
      setDuplicateWarning('');
      setDuplicateBlocking(false);
      return;
    }

    try {
      const res = await CompanyApi.products(Number(selectedCompanyId));
      const products = res.data;

      const duplicate = products.find((p: any) =>
        p.firm?.toLowerCase() === formProduct.firm?.toLowerCase() &&
        p.model?.toLowerCase() === formProduct.model?.toLowerCase() &&
        p.size?.toLowerCase() === formProduct.size?.toLowerCase()
      );

      if (duplicate) {
        setDuplicateWarning(t('productForm.similarProductExists', {
          firm: duplicate.firm,
          model: duplicate.model,
          size: duplicate.size,
          barcode: duplicate.barcode
        }));
        setDuplicateBlocking(false);
      } else {
        setDuplicateWarning('');
        setDuplicateBlocking(false);
      }
    } catch (error) {
      console.error('Error checking duplicates:', error);
      setDuplicateWarning('');
      setDuplicateBlocking(false);
    }
  }, [formProduct.firm, formProduct.model, formProduct.size, selectedCompanyId, t]);

  const checkForDuplicates = async () => {
    if (!formProduct.firm || !formProduct.model || !formProduct.size || !selectedCompanyId) {
      return false;
    }

    try {
      const res = await CompanyApi.products(Number(selectedCompanyId));
      const products = res.data;

      const duplicate = products.find((p: any) =>
        p.firm?.toLowerCase() === formProduct.firm?.toLowerCase() &&
        p.model?.toLowerCase() === formProduct.model?.toLowerCase() &&
        p.size?.toLowerCase() === formProduct.size?.toLowerCase()
      );

      if (duplicate) {
        const duplicateInfo = `${duplicate.firm} - ${duplicate.model} - ${duplicate.size}`;
        const companyInfo = duplicate.companies?.map((c: any) => c.name).join(', ') || t('productForm.noCompany');

        setDuplicateWarning(t('productForm.duplicateProductDetected', {
          product: duplicateInfo,
          name: duplicate.name,
          price: duplicate.price,
          company: companyInfo,
          stock: duplicate.stock || 0
        }));
        setDuplicateBlocking(true);

        return true; // Duplicate found - block creation
      }

      setDuplicateBlocking(false);
      return false; // No duplicate found
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      setDuplicateBlocking(false);
      return false; // Allow creation if there's an error checking
    }
  };

  const handleCreate = async () => {
    if (isSubmitting) return; // Prevent double submission
    setIsSubmitting(true);

    if (!formProduct.firm || formProduct.firm.trim() === '') {
      setError(t('productForm.firmRequired'));
      setIsSubmitting(false);
      return;
    }

    if (!formProduct.model || formProduct.model.trim() === '') {
      setError(t('productForm.modelRequired'));
      setIsSubmitting(false);
      return;
    }

    if (!formProduct.size || formProduct.size.trim() === '') {
      setError(t('productForm.sizeRequired'));
      setIsSubmitting(false);
      return;
    }

    if (!formProduct.category || !formProduct.category.id) {
      setError(t('productForm.categoryRequired'));
      setIsSubmitting(false);
      return;
    }

    if (!selectedCompanyId) {
      setError(t('productForm.companyRequired'));
      setIsSubmitting(false);
      return;
    }

    const isDuplicate = await checkForDuplicates();
    if (isDuplicate) {
      setIsSubmitting(false);
      return;
    }

    const createProduct = { ...formProduct } as any;
    delete createProduct.id;

    if (createProduct.category && typeof createProduct.category === 'object') {
      // Category is already an object, keep it as is
    } else if (createProduct.category) {
      // Category is just an ID, find the full object
      const foundCategory = categories.find((c) => c.id === Number(createProduct.category));
      if (foundCategory) {
        // Convert CompanyCategory to Category format for backend
        createProduct.category = {
          id: foundCategory.id,
          name: foundCategory.name
        };
      }
    }

    // Use the selected company
    if (selectedCompanyId) {
      const selectedCompany = companiesAllowed.find(c => c.id === selectedCompanyId);
      if (selectedCompany) {
        createProduct.companies = [selectedCompany];
      }
    }

    let res;
    try {
      res = await ProductApi.create(createProduct);

      setSuccessMessage(t('productForm.productCreated'));
      setFormProduct({});
      setImage(null);
      setDuplicateWarning('');
      setDuplicateBlocking(false);
      setError('');
      if (onSaved) onSaved(res.data);
    } catch (err: any) {
      console.error('Create product error:', err);

      if (err.response && err.response.data && typeof err.response.data === 'string') {
        const errorMessage = err.response.data;
        if (errorMessage.includes('Product with the same Firm, Model, and Size already exists')) {
          setError(t('productForm.duplicateProductError'));
          setIsSubmitting(false);
          return;
        }
      }

      setError(t('productForm.createFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formProduct.firm || formProduct.firm.trim() === '') {
      setError(t('productForm.firmRequired'));
      return;
    }

    if (!formProduct.model || formProduct.model.trim() === '') {
      setError(t('productForm.modelRequired'));
      return;
    }

    if (!formProduct.size || formProduct.size.trim() === '') {
      setError(t('productForm.sizeRequired'));
      return;
    }

    if (!formProduct.category || !formProduct.category.id) {
      setError(t('productForm.categoryRequired'));
      return;
    }

    if (!selectedCompanyId) {
      setError(t('productForm.companyRequired'));
      return;
    }

    if (formProduct.id) {
      setIsSubmitting(true);

      if (formProduct.category && typeof formProduct.category === 'object') {
        // Category is already an object, keep it as is
      } else if (formProduct.category) {
        // Category is just an ID, find the full object
        const foundCategory = categories.find((c) => c.id === Number(formProduct.category));
        if (foundCategory) {
          // Convert CompanyCategory to Category format for backend
          formProduct.category = {
            id: foundCategory.id,
            name: foundCategory.name
          };
        }
      }

      // Use the selected company for updates
      if (selectedCompanyId) {
        const selectedCompany = companiesAllowed.find(c => c.id === selectedCompanyId);
        if (selectedCompany) {
          formProduct.companies = [selectedCompany];
        }
      } else {
        formProduct.companies = [];
      }

      let res;
      try {
        res = await ProductApi.update(formProduct.id!, formProduct);
        setSuccessMessage(t('productForm.productUpdated'));
        if (image) {
          const formData = new FormData();
          formData.append('file', image);
          await ProductApi.uploadImage(res.data.id || formProduct.id!, formData);
        }
        setFormProduct({});
        setImage(null);
        setDuplicateWarning('');
        setDuplicateBlocking(false);
        setError('');
        if (onSaved) onSaved(res.data);
      } catch (err: any) {
        console.error('Update product error:', err);

        if (err.response && err.response.data && typeof err.response.data === 'string') {
          const errorMessage = err.response.data;
          if (errorMessage.includes('Product with the same Firm, Model, and Size already exists')) {
            setError(t('productForm.duplicateProductError'));
            setIsSubmitting(false);
            return;
          }
        }

        setError(t('productForm.updateFailed'));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      await handleCreate();
    }
  };

  useEffect(() => {
    // Load companies available to the current user.
    PersonalCabinetApi.getPersonalCompanies().then((res: any) => setCompaniesAllowed(res.data)).catch((err: any) => console.error('Failed to load companies', err));
  }, []);

  // Load categories when selectedCompanyId changes
  useEffect(() => {
    if (selectedCompanyId) {
      PersonalCabinetApi.getCompanyCategories(selectedCompanyId)
        .then((res: any) => setCategories(res.data))
        .catch((err: any) => {
          console.error('Failed to load company categories', err);
          setCategories([]);
        });
    } else {
      setCategories([]);
    }
  }, [selectedCompanyId]);

  // Ensure category is properly matched when categories are loaded and product exists
  useEffect(() => {
    if (categories.length > 0 && formProduct.category && typeof formProduct.category === 'object') {
      // Find the matching category from the loaded categories
      const matchingCategory = categories.find(c => c.id === formProduct.category?.id);
      if (matchingCategory && matchingCategory !== formProduct.category) {
        setFormProduct(prev => ({
          ...prev,
          category: matchingCategory
        }));
      }
    }
  }, [categories, formProduct.category]);

  useEffect(() => {
    if (product) {
      setFormProduct(product);
    } else if (lastCompanyProduct && selectedCompanyId && !product) {
      const template = { ...lastCompanyProduct };
      template.id = undefined;
      template.name = '';
      template.description = '';
      template.stock = 0;
      // Set the selected company for new products
      if (selectedCompanyId) {
        const selectedCompany = companiesAllowed.find(c => c.id === selectedCompanyId);
        if (selectedCompany) {
          template.companies = [selectedCompany];
        }
      }
      setFormProduct(template);
    } else if (latestProduct && !product) {
      const template = { ...latestProduct };
      template.id = undefined;
      template.name = '';
      template.description = '';
      template.stock = 0;
      // Set the selected company for new products
      if (selectedCompanyId) {
        const selectedCompany = companiesAllowed.find(c => c.id === selectedCompanyId);
        if (selectedCompany) {
          template.companies = [selectedCompany];
        }
      }
      setFormProduct(template);
    } else {
      // For completely new products, set the selected company
      const initialProduct: any = {};
      if (selectedCompanyId) {
        const selectedCompany = companiesAllowed.find(c => c.id === selectedCompanyId);
        if (selectedCompany) {
          initialProduct.companies = [selectedCompany];
        }
      }
      setFormProduct(initialProduct);
    }
  }, [product, latestProduct, lastCompanyProduct, selectedCompanyId, companiesAllowed]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkDuplicatesRealTime();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [checkDuplicatesRealTime]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setError('');
    setSuccessMessage('');
    if (['firm', 'model', 'size'].includes(name)) {
      setDuplicateWarning('');
      setDuplicateBlocking(false);
    }

    if (name === 'category') {
      setFormProduct(prev => ({
        ...prev,
        category: categories.find(c => c.id === Number(value))
      }));
    } else {
      setFormProduct(prev => ({ ...prev, [name]: value }));
    }
  };



  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setImage(e.target.files[0]);
  };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px #0001', padding: 32, marginBottom: 32 }}>
      <form onSubmit={handleSubmit}>
        <h2 style={{ color: '#1976d2', marginBottom: 24 }}>{formProduct.id ? t('productForm.editProduct') : t('productForm.addProduct')}</h2>

        {error && (
          <div style={{
            background: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: 6,
            padding: '12px 16px',
            margin: '0 0 16px 0',
            color: '#c62828',
            fontSize: '14px',
            whiteSpace: 'pre-line'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            background: '#e8f5e8',
            border: '1px solid #4caf50',
            borderRadius: 6,
            padding: '12px 16px',
            margin: '0 0 16px 0',
            color: '#2e7d32',
            fontSize: '14px'
          }}>
            {successMessage}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label>
            {t('productForm.category')}:
            <select
              name="category"
              value={(formProduct.category as any)?.id || ''}
              onChange={handleChange}
              style={{ 
                width: '100%', 
                padding: 8, 
                borderRadius: 6, 
                border: '1px solid #1976d2', 
                marginTop: 4,
                backgroundColor: (!formProduct.category || !formProduct.category.id) ? '#fff3cd' : '#fff'
              }}
              disabled={!selectedCompanyId}
              required
            >
              <option value="">
                {!selectedCompanyId 
                  ? t('productForm.selectCompanyFirst')
                  : t('productForm.selectCategory')
                }
              </option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {!selectedCompanyId && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {t('productForm.categoriesFromSelectedCompany')}
              </div>
            )}
          </label>
          <label>
            {t('productForm.firm')}:
            <input name="firm" value={formProduct.firm || ''} onChange={handleChange} required style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }} />
          </label>
          <label>
            {t('productForm.model')}:
            <input name="model" value={formProduct.model || ''} onChange={handleChange} required style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }} />
          </label>
          <label>
            {t('productForm.size')}:
            <input name="size" value={formProduct.size || ''} onChange={handleChange} required style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }} />
          </label>
          {duplicateWarning && (
            <div style={{
              background: duplicateBlocking ? '#ffebee' : '#fff3cd',
              border: duplicateBlocking ? '1px solid #f44336' : '1px solid #ffeaa7',
              borderRadius: 6,
              padding: '12px 16px',
              margin: '8px 0',
              color: duplicateBlocking ? '#c62828' : '#856404',
              fontSize: '14px',
              whiteSpace: 'pre-line',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px', marginTop: '2px' }}>
                {duplicateBlocking ? '⛔' : '⚠️'}
              </span>
              <span>{duplicateWarning}</span>
            </div>
          )}
          <label>
            {t('productForm.price')}:
            <input name="price" type="number" step="0.01" value={formProduct.price || ''} onChange={handleChange} required style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }} />
          </label>

          {/* E-commerce Cost Tracking Section */}
          <div style={{ marginTop: 20, marginBottom: 20, padding: 15, backgroundColor: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0' }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#1976d2', fontSize: '14px', fontWeight: 600 }}>
              {t('productForm.costInformation')}
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <label style={{ marginBottom: 0 }}>
                {t('productForm.acquisitionCost')}:
                <input 
                  name="acquisitionCost" 
                  type="number" 
                  step="0.01" 
                  value={formProduct.acquisitionCost || ''} 
                  onChange={handleChange} 
                  placeholder="0.00"
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} 
                />
              </label>
              <label style={{ marginBottom: 0 }}>
                {t('productForm.deliveryCost')}:
                <input 
                  name="deliveryCost" 
                  type="number" 
                  step="0.01" 
                  value={formProduct.deliveryCost || ''} 
                  onChange={handleChange} 
                  placeholder="0.00"
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} 
                />
              </label>
              <label style={{ marginBottom: 0 }}>
                {t('productForm.adsCost')}:
                <input 
                  name="adsCost" 
                  type="number" 
                  step="0.01" 
                  value={formProduct.adsCost || ''} 
                  onChange={handleChange} 
                  placeholder="0.00"
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} 
                />
              </label>
              <label style={{ marginBottom: 0 }}>
                {t('productForm.otherCosts')}:
                <input 
                  name="otherCosts" 
                  type="number" 
                  step="0.01" 
                  value={formProduct.otherCosts || ''} 
                  onChange={handleChange} 
                  placeholder="0.00"
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }} 
                />
              </label>
            </div>
            {(formProduct.price && (formProduct.acquisitionCost || formProduct.deliveryCost || formProduct.adsCost || formProduct.otherCosts)) && (
              <div style={{ marginTop: 15, padding: 10, backgroundColor: '#e8f5e9', borderRadius: 6, fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span><strong>{t('productForm.totalCost')}:</strong></span>
                  <span>{((Number(formProduct.acquisitionCost) || 0) + (Number(formProduct.deliveryCost) || 0) + (Number(formProduct.adsCost) || 0) + (Number(formProduct.otherCosts) || 0)).toFixed(2)} lei</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span><strong>{t('productForm.profitPerUnit')}:</strong></span>
                  <span style={{ color: ((Number(formProduct.price) || 0) - ((Number(formProduct.acquisitionCost) || 0) + (Number(formProduct.deliveryCost) || 0) + (Number(formProduct.adsCost) || 0) + (Number(formProduct.otherCosts) || 0))) >= 0 ? 'green' : 'red' }}>
                    {((Number(formProduct.price) || 0) - ((Number(formProduct.acquisitionCost) || 0) + (Number(formProduct.deliveryCost) || 0) + (Number(formProduct.adsCost) || 0) + (Number(formProduct.otherCosts) || 0))).toFixed(2)} lei
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span><strong>{t('productForm.profitMargin')}:</strong></span>
                  <span style={{ color: (((Number(formProduct.price) || 0) - ((Number(formProduct.acquisitionCost) || 0) + (Number(formProduct.deliveryCost) || 0) + (Number(formProduct.adsCost) || 0) + (Number(formProduct.otherCosts) || 0))) / (Number(formProduct.price) || 1) * 100) >= 30 ? 'green' : (((Number(formProduct.price) || 0) - ((Number(formProduct.acquisitionCost) || 0) + (Number(formProduct.deliveryCost) || 0) + (Number(formProduct.adsCost) || 0) + (Number(formProduct.otherCosts) || 0))) / (Number(formProduct.price) || 1) * 100) >= 15 ? 'orange' : 'red' }}>
                    {(((Number(formProduct.price) || 0) - ((Number(formProduct.acquisitionCost) || 0) + (Number(formProduct.deliveryCost) || 0) + (Number(formProduct.adsCost) || 0) + (Number(formProduct.otherCosts) || 0))) / (Number(formProduct.price) || 1) * 100).toFixed(2)}%
                  </span>
                </div>
              </div>
            )}
          </div>

          <label>
            {t('productForm.name')}:
            <input name="name" value={formProduct.name || ''} onChange={handleChange} required style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }} />
          </label>
          <label>
            {t('productForm.description')}:
            <input name="description" value={formProduct.description || ''} onChange={handleChange} style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }} />
          </label>
          <label>
            {t('productForm.customBarcode')}:
            <input
              name="customBarcode"
              value={formProduct.customBarcode || ''}
              onChange={handleChange}
              placeholder={t('productForm.autoGenerateCodePlaceholder')}
              style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: 4, fontStyle: 'italic' }}>
              {t('productForm.customBarcodeHelp')}
            </div>
          </label>
          <label>
            {t('productForm.customQrCode')}:
            <input
              name="customQrCode"
              value={formProduct.customQrCode || ''}
              onChange={handleChange}
              placeholder={t('productForm.autoGenerateCodePlaceholder')}
              style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #1976d2', marginTop: 4 }}
            />
            <div style={{ fontSize: '11px', color: '#666', marginTop: 4, fontStyle: 'italic' }}>
              {t('productForm.customQrCodeHelp')}
            </div>
          </label>
          <label>
            {t('productForm.headerText')}:
            <textarea
              name="headerText"
              value={formProduct.headerText || ''}
              onChange={handleChange}
              placeholder={t('productForm.headerTextPlaceholder')}
              rows={4}
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 6,
                border: '1px solid #1976d2',
                marginTop: 4,
                resize: 'vertical',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}
            />
            <div style={{
              fontSize: '11px',
              color: '#666',
              marginTop: '4px',
              fontStyle: 'italic'
            }}>
              {t('productForm.formattingTip')}
            </div>
          </label>
          <label>
            {t('productForm.companies')}:
            <div style={{ 
              width: '100%', 
              padding: 8, 
              borderRadius: 6, 
              border: '1px solid #ccc', 
              marginTop: 4,
              backgroundColor: '#f5f5f5',
              color: '#666',
              fontStyle: 'italic'
            }}>
              {selectedCompanyId 
                ? companiesAllowed.find(c => c.id === selectedCompanyId)?.name || t('productForm.unknownCompany')
                : t('productForm.selectCompanyFirst')
              }
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {t('productForm.companyAutoAssigned')}
            </div>
          </label>
          <label>
            {t('productForm.image')}:
            <input type="file" accept="image/*" onChange={handleImageChange} style={{ marginTop: 4 }} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
          {formProduct.id && (
            <button
              type="submit"
              disabled={isSubmitting}
              title={t('productForm.update')}
              style={{
                background: isSubmitting ? '#ccc' : '#1976d2',
                border: 'none',
                borderRadius: '50%',
                padding: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              {isSubmitting ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeDasharray="15 5" strokeDashoffset="0">
                    <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="1s" repeatCount="indefinite" />
                  </circle>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z" />
                </svg>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleCreate}
            disabled={isSubmitting || duplicateBlocking}
            title={t('productForm.create')}
            style={{
              background: (isSubmitting || duplicateBlocking) ? '#ccc' : '#1976d2',
              border: 'none',
              borderRadius: '50%',
              padding: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (isSubmitting || duplicateBlocking) ? 'not-allowed' : 'pointer',
              opacity: (isSubmitting || duplicateBlocking) ? 0.6 : 1
            }}
          >
            {isSubmitting ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeDasharray="15 5" strokeDashoffset="0">
                  <animateTransform attributeName="transform" type="rotate" values="0 12 12;360 12 12" dur="1s" repeatCount="indefinite" />
                </circle>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                <path d="M12 7v10M7 12h10" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

